import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import ReportPageWrap from "@/components/reports/ReportPageWrap";
import {
  PageHeader,
  Card,
  Pill,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useReportBranch } from "@/contexts/ReportBranchContext";

import api from "@/lib/api";
import { aed } from "@/lib/format";
import { printProfitLoss } from "@/lib/profitLossPrint";
import { cn } from "@/lib/utils";
import { ReportKpiStrip } from "@/components/reports/ReportKpiStrip";
import { ReportTableCard } from "@/components/reports/ReportTableCard";
import ReportExportButtons from "@/components/reports/ReportExportButtons";

const defaultFromDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const today = () => new Date().toISOString().slice(0, 10);

function SectionBlock({ title, sections, tone, onRowClick }) {
  if (!sections?.length) {
    return (
      <Card className="p-5">
        <SectionTitle className="!mb-2">{title}</SectionTitle>
        <p className="text-sm text-slate-500">No activity in this period.</p>
      </Card>
    );
  }

  return (
    <ReportTableCard
      title={title}
      dense={false}
      actions={
        tone === "green" ? (
          <TrendingUp size={16} className="text-emerald-600" />
        ) : (
          <TrendingDown size={16} className="text-amber-600" />
        )
      }
      className="mb-0"
    >
      <div className="divide-y">
        {sections.map((section) => (
          <div key={section.schedule_id ?? section.schedule_name} className="px-3 py-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[13px] font-semibold text-slate-800">{section.schedule_name}</h3>
              <Pill tone={tone}>{aed(section.subtotal)}</Pill>
            </div>
            <div className="report-table">
              <table>
                <tbody>
                  {section.accounts.map((a) => (
                    <tr
                      key={a.account_id}
                      className="cursor-pointer"
                      onClick={() => onRowClick(a.account_id)}
                    >
                      <td className="font-mono text-blue-700 w-[14%]">{a.code}</td>
                      <td className="text-slate-700">{a.name}</td>
                      <td className="text-right font-mono font-medium w-[18%]">{aed(a.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </ReportTableCard>
  );
}

export default function ProfitLossReport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { branchId, allBranches, reportParams, canQuery } = useReportBranch();
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(today);

  const { data: report, isLoading, isFetching } = useQuery({
    queryKey: ["profit-loss", branchId, fromDate, toDate],
    queryFn: async () => {
      const { data: res } = await api.get("/profit-loss", {
        params: { ...reportParams, from_date: fromDate, to_date: toDate },
      });
      return res;
    },
    enabled: Boolean(canQuery && fromDate && toDate),
  });

  const openLedger = (accountId) => {
    navigate(`/accounts/ledger?account_id=${accountId}&from_date=${fromDate}&to_date=${toDate}`);
  };

  return (
    <ReportPageWrap>
      <PageHeader
        title="Profit & Loss"
        subtitle="Income and expense schedules for the selected period"
        actions={
          <ReportExportButtons
            disabled={!report}
            title="Profit & Loss"
            filename="profit-loss"
            printTestId="pl-print"
            onPrint={() => printProfitLoss(report, "Profit & Loss", { branchId, allBranches })}
          />
        }
      />

      <Card className="p-5 mb-4">
        <SectionTitle className="!mb-3">Period</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 max-w-xl">
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">From Date</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9"
              data-testid="pl-from"
            />
          </div>
          <div>
            <Label className="text-[12px] text-slate-600 mb-1 block">To Date</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9"
              data-testid="pl-to"
            />
          </div>
        </div>
      </Card>

      {report && (
        <ReportKpiStrip
          items={[
            {
              label: "Total Income",
              value: <span data-testid="pl-income">{aed(report.total_income)}</span>,
            },
            {
              label: "Total Expenses",
              value: <span data-testid="pl-expenses">{aed(report.total_expenses)}</span>,
            },
            {
              label: "Net Profit / (Loss)",
              value: (
                <span
                  className={cn(report.net_profit >= 0 ? "text-emerald-700" : "text-red-600")}
                  data-testid="pl-net"
                >
                  {aed(report.net_profit)}
                </span>
              ),
              emphasize: true,
            },
          ]}
        />
      )}

      {(isLoading || isFetching) && (
        <div className="flex items-center justify-center py-10 text-slate-500 text-sm mb-4">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading report…
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SectionBlock
          title="Income"
          sections={report?.income_sections}
          tone="green"
          onRowClick={openLedger}
        />
        <SectionBlock
          title="Expenses"
          sections={report?.expense_sections}
          tone="amber"
          onRowClick={openLedger}
        />
      </div>
    </ReportPageWrap>
  );
}
